import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Trophy, Target, TrendingUp, Crown, ArrowRight, Sparkles } from "lucide-react";

export default function PracticeDashboard() {
  const [stats, setStats] = useState(null);
  const [board, setBoard] = useState([]);

  useEffect(() => {
    api.get("/api/practice/stats").then(({ data }) => setStats(data));
    api.get("/api/practice/leaderboard").then(({ data }) => setBoard(data.top || []));
  }, []);

  if (!stats) return <div className="p-12 text-center text-[#4B5563]">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="practice-dashboard">
      <h1 className="font-display font-black text-3xl md:text-4xl">My Practice Progress</h1>

      <div className="mt-6 grid sm:grid-cols-3 gap-4">
        <Stat icon={Target}    label="Tests completed"     value={stats.tests_completed} />
        <Stat icon={Trophy}    label="Questions attempted" value={stats.questions_attempted} />
        <Stat icon={TrendingUp} label="Last activity"      value={stats.last_activity ? new Date(stats.last_activity).toLocaleDateString() : "—"} />
      </div>

      {/* PER-TEST PERFORMANCE */}
      <div className="mt-8 bg-white border border-[#E5E7EB] rounded-2xl p-6">
        <div className="font-display font-bold text-lg">By test</div>
        {stats.per_test.length === 0 && <div className="text-sm text-[#4B5563] mt-3">Take your first practice test to see stats here.</div>}
        <div className="mt-4 space-y-4">
          {stats.per_test.map((t) => (
            <div key={t.test_key} data-testid={`pt-${t.test_key}`} className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold">{t.test_name}</div>
                <div className="text-xs text-[#4B5563]">{t.attempts} attempts · avg {t.avg_pct}% · best {t.best_pct}%</div>
              </div>
              <Link to={`/practice/${t.test_key.replace("_", "-")}`} className="text-sm font-semibold text-[#D92D20] inline-flex items-center gap-1">
                Practice more <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* STRENGTHS / WEAKNESSES */}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        <div className="bg-[#D1FAE5] border border-[#A7F3D0] rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-[#064E3B] font-bold">Strong categories</div>
          {stats.strong_categories.length === 0 && <div className="text-sm text-[#064E3B]/70 mt-2">Complete more questions to see strengths.</div>}
          <ul className="mt-2 space-y-1 text-sm">
            {stats.strong_categories.map((c) => (
              <li key={c.category} className="flex justify-between"><span>{c.category}</span><b>{c.pct}%</b></li>
            ))}
          </ul>
        </div>
        <div className="bg-[#FEE2E2] border border-[#FECACA] rounded-2xl p-5">
          <div className="text-xs uppercase tracking-widest text-[#D92D20] font-bold">Needs improvement</div>
          {stats.weak_categories.length === 0 && <div className="text-sm text-[#D92D20]/70 mt-2">No weak spots — nice work!</div>}
          <ul className="mt-2 space-y-1 text-sm">
            {stats.weak_categories.map((c) => (
              <li key={c.category} className="flex justify-between"><span>{c.category}</span><b>{c.pct}%</b></li>
            ))}
          </ul>
        </div>
      </div>

      {/* LEADERBOARD */}
      <div className="mt-8 bg-white border border-[#E5E7EB] rounded-2xl p-6">
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-[#F59E0B]" />
          <div className="font-display font-bold text-lg">Top scores this week</div>
        </div>
        <table className="w-full text-left text-sm mt-3" data-testid="leaderboard">
          <thead className="text-xs uppercase tracking-widest text-[#4B5563]">
            <tr><th className="py-2">#</th><th>Name</th><th>Test</th><th className="text-right">Score</th></tr>
          </thead>
          <tbody>
            {board.map((r, i) => (
              <tr key={`${r.first_name}-${i}`} className="border-t border-[#E5E7EB]">
                <td className="py-2 font-bold">{i + 1}</td>
                <td>{r.first_name}</td>
                <td className="text-xs text-[#4B5563]">{r.test_name}</td>
                <td className="text-right font-bold">{r.score_pct}%</td>
              </tr>
            ))}
            {board.length === 0 && <tr><td colSpan="4" className="py-6 text-center text-[#4B5563]">No scores yet — be the first!</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-8 bg-[#064E3B] text-white rounded-3xl p-6 text-center">
        <Sparkles className="w-8 h-8 mx-auto" />
        <div className="font-display font-bold text-2xl mt-2">Want unlimited AI tutoring?</div>
        <p className="text-sm text-white/80 mt-1">Teacher4Me Free Forever — daily AI tutor questions, parent reports, and more.</p>
        <Link to="/tutor-landing" data-testid="dashboard-tutoring-cta" className="btn-primary inline-flex items-center gap-2 mt-4">
          Try free <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-5">
      <div className="w-9 h-9 rounded-xl bg-[#FEF2F2] text-[#D92D20] flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div className="font-display font-black text-2xl mt-3">{value}</div>
      <div className="text-xs uppercase tracking-widest text-[#4B5563] mt-1">{label}</div>
    </div>
  );
}
